import { useCallback, useMemo, useState } from 'react'
import type { AssetVisibilityContext, BuxusOperatorPayload } from '@buxus/shared'
import { buildAssetCatalogEntries } from '@buxus/shared'
import { MapSurface } from './MapSurface'
import type { PipelineMapOperatorDirectoryEntry } from './mapPipelinePopupDom'
import { AssetIndexList } from './components/assets/AssetIndexList'
import { AssetsMapRailOverlay } from './components/assets/AssetsMapRailOverlay'
import {
  trackButtonPressed,
  type ScreenInteractionContext,
} from './platform/analytics/interactionAnalytics'

type AllAssetsViewMode = 'list' | 'map'

const VIEW_MODE_STORAGE_KEY = 'buxus.web.allAssetsViewMode'

type AllAssetsHubScreenProps = {
  payload: BuxusOperatorPayload
  resolveContext: AssetVisibilityContext
  stakeholderCode: string | null
  jurisdictionCountyStateKeys: ReadonlySet<string> | null
  pipelineOperatorDirectory?: readonly PipelineMapOperatorDirectoryEntry[]
  onViewPipelineOperator?: (operatorId: string) => void
  onSelectAsset: (assetId: string) => void
  onFocusAssetInSidebar: (assetId: string) => void
  contextAssetId?: string | null
  analytics?: ScreenInteractionContext
}

function readStoredViewMode(): AllAssetsViewMode {
  try {
    return sessionStorage.getItem(VIEW_MODE_STORAGE_KEY) === 'map' ? 'map' : 'list'
  } catch {
    return 'list'
  }
}

function persistViewMode(mode: AllAssetsViewMode): void {
  try {
    sessionStorage.setItem(VIEW_MODE_STORAGE_KEY, mode)
  } catch {
    /* ignore */
  }
}

export function AllAssetsHubScreen({
  payload,
  resolveContext,
  stakeholderCode,
  jurisdictionCountyStateKeys,
  pipelineOperatorDirectory,
  onViewPipelineOperator,
  onSelectAsset,
  onFocusAssetInSidebar,
  contextAssetId = null,
  analytics,
}: AllAssetsHubScreenProps) {
  const [viewMode, setViewModeState] = useState<AllAssetsViewMode>(readStoredViewMode)

  const setViewMode = useCallback(
    (mode: AllAssetsViewMode) => {
      setViewModeState((prev) => {
        if (prev === mode) return prev
        if (analytics) {
          trackButtonPressed({
            ...analytics,
            button: mode === 'map' ? 'Map' : 'List',
          })
        }
        persistViewMode(mode)
        return mode
      })
    },
    [analytics],
  )

  const catalog = useMemo(
    () => buildAssetCatalogEntries(payload, resolveContext),
    [payload, resolveContext],
  )

  return (
    <div
      className={[
        'all-assets-hub',
        viewMode === 'map' ? 'all-assets-hub--map-mode' : 'all-assets-hub--list-mode',
      ].join(' ')}
    >
      <div className="all-assets-mode-bar" role="tablist" aria-label="All Assets view">
        <button
          type="button"
          role="tab"
          aria-selected={viewMode === 'list'}
          className={viewMode === 'list' ? 'all-assets-mode-btn active' : 'all-assets-mode-btn'}
          onClick={() => setViewMode('list')}
        >
          List
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={viewMode === 'map'}
          className={viewMode === 'map' ? 'all-assets-mode-btn active' : 'all-assets-mode-btn'}
          onClick={() => setViewMode('map')}
        >
          Map
        </button>
      </div>

      {viewMode === 'list' ? (
        <AssetIndexList entries={catalog} onSelect={onSelectAsset} />
      ) : (
        <MapSurface variant="fluid" className="all-assets-map-surface">
          <AssetsMapRailOverlay
            payload={payload}
            resolveContext={resolveContext}
            stakeholderCode={stakeholderCode}
            jurisdictionCountyStateKeys={jurisdictionCountyStateKeys}
            pipelineOperatorDirectory={pipelineOperatorDirectory}
            onViewPipelineOperator={onViewPipelineOperator}
            onFocusAssetInSidebar={onFocusAssetInSidebar}
            contextAssetId={contextAssetId}
            mapKey="all-assets-map-mode"
            analytics={analytics}
          />
        </MapSurface>
      )}
    </div>
  )
}
